import React, { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'en' | 'ur';

export interface ServiceItem {
  title: string;
  description: string;
  iconName: string;
  tags: string[];
}

export interface TestimonialItem {
  id: number;
  name: string;
  role: string;
  content: string;
  avatar?: string;
}

export interface Translations {
  nav: {
    home: string;
    about: string;
    skills: string;
    services: string;
    estimator: string;
    work: string;
    portfolio: string;
    aiVideos: string;
    testimonials: string;
    contact: string;
    letsTalk: string;
    shortcuts: string;
  };
  hero: {
    badgeAvailable: string;
    badgeReadTime: string;
    name: string;
    titleLine: string;
    fullParagraph: string;
    btnExplore: string;
    btnContact: string;
    portraitBadge: string;
  };
  sectionDividers: {
    about: string;
    skills: string;
    services: string;
    estimator: string;
    portfolio: string;
    aiVideos: string;
    testimonials: string;
    whyChooseMe: string;
    contact: string;
  };
  about: {
    badge: string;
    title: string;
    titleHighlight: string;
    subtitle: string;
    headingPrefix: string;
    headingHighlight: string;
    bio: string;
    tabDev: string;
    tabVideo: string;
    tabDesign: string;
    devTitle: string;
    devDesc: string;
    devTags: string[];
    videoTitle: string;
    videoDesc: string;
    videoTags: string[];
    designTitle: string;
    designDesc: string;
    designTags: string[];
    statYears: string;
    statDeployments: string;
    btnStart: string;
    cardName: string;
    cardRole: string;
    tabStory: string;
    tabPhilosophy: string;
    tabTech: string;
    storyP1: string;
    storyP2: string;
    storyP3: string;
    statExp: string;
    statExpLabel: string;
    statProjects: string;
    statProjectsLabel: string;
    statSatisfaction: string;
    statSatisfactionLabel: string;
    quote: string;
    quoteAuthor: string;
  };
  skills: {
    badge: string;
    title: string;
    subtitle: string;
    headingPrefix: string;
    headingHighlight: string;
    subheading: string;
    proficiency: string;
  };
  services: {
    badge: string;
    title: string;
    subtitle: string;
    headingPrefix: string;
    headingHighlight: string;
    subheading: string;
    btnOrder: string;
    inquireNow: string;
    featuresIncluded: string;
    items: ServiceItem[];
  };
  calculator: {
    badge: string;
    title: string;
    subtitle: string;
    step1Title: string;
    step2Title: string;
    step3Title: string;
    timeline: string;
    standardSpeed: string;
    expressSpeed: string;
    relaxedSpeed: string;
    estimatedCost: string;
    estimatedDays: string;
    ctaButton: string;
    resetButton: string;
  };
  portfolio: {
    badge: string;
    title: string;
    subtitle: string;
    filterAll: string;
    filterDev: string;
    filterDesign: string;
    filterWp: string;
    viewLive: string;
  };
  aiVideos: {
    badge: string;
    title: string;
    subtitle: string;
    filterAll: string;
    filterCommercials: string;
    filterReels: string;
    filterProducts: string;
    playVideo: string;
    closeModal: string;
  };
  testimonials: {
    badge: string;
    title: string;
    subtitle: string;
    ratingText: string;
    verifiedBadge: string;
    items: TestimonialItem[];
  };
  whyChooseMe: {
    badge: string;
    title: string;
    subtitle: string;
    description: string;
    b1Title: string;
    b1Desc: string;
    b2Title: string;
    b2Desc: string;
    b3Title: string;
    b3Desc: string;
    b4Title: string;
    b4Desc: string;
  };
  contact: {
    badge: string;
    title: string;
    subtitle: string;
    nameLabel: string;
    namePlaceholder: string;
    emailLabel: string;
    emailPlaceholder: string;
    phoneLabel: string;
    phonePlaceholder: string;
    serviceLabel: string;
    serviceSelectPlaceholder: string;
    messageLabel: string;
    messagePlaceholder: string;
    btnSend: string;
    btnSending: string;
    successTitle: string;
    successMessage: string;
    errorMessage: string;
    directContact: string;
    whatsappChat: string;
  };
  footer: {
    description: string;
    quickLinks: string;
    servicesTitle: string;
    contactTitle: string;
    rightsReserved: string;
    developerCredit: string;
  };
  shortcuts: {
    title: string;
    subtitle: string;
    pressToToggle: string;
    escToClose: string;
    navigatingTo: string;
  };
}

export const TRANSLATIONS: Record<Language, Translations> = {
  en: {
    nav: {
      home: 'Home',
      about: 'About',
      skills: 'Skills',
      services: 'Services',
      estimator: 'Estimator',
      work: 'Work',
      portfolio: 'Portfolio',
      aiVideos: 'AI Videos',
      testimonials: 'Testimonials',
      contact: 'Contact',
      letsTalk: "Let's Talk",
      shortcuts: 'Shortcuts',
    },
    hero: {
      badgeAvailable: 'Available for New Projects',
      badgeReadTime: '3 Min Read',
      name: 'Taibe Javed',
      titleLine: 'WordPress Web Developer | AI & Coded Websites | AI Video Editor',
      fullParagraph: 'Specialized in crafting high-converting WordPress websites, AI-powered custom coded web solutions (HTML/CSS/JS/React), creative AI video editing (Reels/Shorts/CapCut), and iconic graphic design.',
      btnExplore: 'Explore Work',
      btnContact: 'Get in Touch',
      portraitBadge: 'WordPress • AI • Video • Design',
    },
    sectionDividers: {
      about: 'About Me',
      skills: 'Core Skills',
      services: 'Services',
      estimator: 'Project Cost Estimator',
      portfolio: 'Featured Work',
      aiVideos: 'AI Video Production',
      testimonials: 'Client Testimonials',
      whyChooseMe: 'Why Choose Me',
      contact: 'Contact',
    },
    about: {
      badge: 'About My Journey',
      title: 'Crafting Digital Experiences with',
      titleHighlight: 'Precision & AI',
      subtitle: 'Passionate full-stack WordPress developer, custom web coder, and AI video specialist committed to building impactful digital solutions.',
      headingPrefix: 'Crafting Digital Solutions with',
      headingHighlight: 'Precision & AI Speed',
      bio: "I'm Taibe Javed, a dedicated WordPress Developer, Custom Coded Web Creator, and AI Video Producer focused on high conversion, lightning performance, and pixel-perfect design.",
      tabDev: 'Web Development',
      tabVideo: 'AI Video Production',
      tabDesign: 'UI & Branding',
      devTitle: 'WordPress & Custom Code Architecture',
      devDesc: 'Engineering modern responsive WordPress portals with Elementor Pro, custom WooCommerce functionality, and performant React applications.',
      devTags: ['WordPress', 'Elementor Pro', 'WooCommerce', 'React.js', 'HTML5/CSS3', 'SEO Speed'],
      videoTitle: 'AI Video Media & Social Shorts',
      videoDesc: 'Producing high-impact AI commercial video ads, TikToks, Instagram Reels, and YouTube Shorts with CapCut and synced motion subtitles.',
      videoTags: ['AI Video Generation', 'CapCut Pro', 'Instagram Reels', 'Auto Subtitles', 'AI Voiceover', 'Viral Hooks'],
      designTitle: 'Logo & Graphic Branding Suite',
      designDesc: 'Developing iconic brand identities, bespoke logos, Canva templates, and promotional social media kits in Adobe Illustrator and Photoshop.',
      designTags: ['Logo Design', 'Brand Identity', 'Canva Pro', 'Illustrator', 'Photoshop', 'Social Assets'],
      statYears: 'Year of Expertise',
      statDeployments: 'Deployments Completed',
      btnStart: 'Start a Project',
      cardName: 'M. Taibe Javed',
      cardRole: 'Web Architect & Media Creator',
      tabStory: 'My Story',
      tabPhilosophy: 'Philosophy',
      tabTech: 'Tech Stack',
      storyP1: "Hello! I'm Taibe Javed, a dedicated WordPress Developer, Custom Coded Web Creator, and AI Video Producer with an obsession for speed, design, and conversion.",
      storyP2: 'With years of hands-on experience in building robust WordPress portals, custom coded responsive web applications, and viral AI video commercials, I bring ideas to life with technical excellence.',
      storyP3: 'Whether creating tailored e-commerce platforms, custom web tools, or eye-catching Reels and commercial promos, I focus on clean code, seamless user experience, and high performance.',
      statExp: '4+',
      statExpLabel: 'Years of Experience',
      statProjects: '50+',
      statProjectsLabel: 'Successful Projects',
      statSatisfaction: '100%',
      statSatisfactionLabel: 'Client Satisfaction',
      quote: '"Great digital experiences are born at the intersection of creative design and flawless technical execution."',
      quoteAuthor: 'Taibe Javed',
    },
    skills: {
      badge: 'Technical Capabilities',
      title: 'Core Technical Expertise',
      subtitle: 'Specialized skills honed across modern web technologies, AI workflows, and creative design suites.',
      headingPrefix: 'Core Technical',
      headingHighlight: 'Capabilities & Stack',
      subheading: 'Specialized expertise across modern web architecture, AI-powered generation workflows, and high-converting creative media.',
      proficiency: 'Proficiency',
    },
    services: {
      badge: 'What I Offer',
      title: 'Services Tailored for Growth',
      subtitle: 'Comprehensive digital development, custom web coding, and creative AI video production for businesses worldwide.',
      headingPrefix: 'Services Built for',
      headingHighlight: 'Real Business Growth',
      subheading: 'Comprehensive digital development, custom web coding, and creative AI video production for clients and enterprises worldwide.',
      btnOrder: 'Order Service',
      inquireNow: 'Inquire Now',
      featuresIncluded: 'Key Features Included',
      items: [
        {
          title: 'WordPress Development',
          description: 'Expert WordPress web developer specializing in custom themes, advanced Elementor Pro integrations, WooCommerce stores, and secure custom setups. Fast, responsive, and easy to manage.',
          iconName: 'Globe',
          tags: ['WordPress', 'Elementor', 'WooCommerce'],
        },
        {
          title: 'AI & Custom Coded Websites',
          description: 'Building modern, high-performance websites leveraging cutting-edge AI tools combined with custom clean coding (HTML, CSS, JavaScript, React). Uniquely designed for maximum speed and conversion.',
          iconName: 'Code2',
          tags: ['AI Builder', 'Custom Code', 'React'],
        },
        {
          title: 'AI Video Editing & Shorts',
          description: 'Creative AI video editor producing engaging short-form content, Instagram Reels, TikToks, and promotional videos using AI generation, CapCut, automated captions, and sound design.',
          iconName: 'Zap',
          tags: ['AI Video', 'CapCut', 'Reels & Shorts'],
        },
        {
          title: 'Logo & Graphic Design',
          description: 'Creating standout brand identities, custom logos, and eye-catching visual assets using Canva, Adobe Illustrator, and Photoshop to make your brand memorable.',
          iconName: 'PenTool',
          tags: ['Branding', 'Canva', 'Illustrator'],
        },
        {
          title: 'Website Redesign & Revamp',
          description: 'Transforming outdated or slow websites into modern, high-converting digital portals with responsive mobile layouts and refreshed UI/UX aesthetics.',
          iconName: 'Layers',
          tags: ['Redesign', 'Modern UI', 'Mobile First'],
        },
        {
          title: 'Speed & SEO Optimization',
          description: 'Boosting website load speed under 2 seconds and setting up search engine SEO structure to rank higher on Google and improve Lighthouse metrics.',
          iconName: 'Zap',
          tags: ['Lighthouse', 'Speed Boost', 'SEO'],
        },
      ],
    },
    calculator: {
      badge: 'Interactive Pricing',
      title: 'Project Cost Estimator',
      subtitle: 'Customize your project scope and get an immediate transparent cost and delivery estimate.',
      step1Title: '1. Select Base Service Tier',
      step2Title: '2. Select Add-on Enhancements',
      step3Title: '3. Choose Timeline & Estimate',
      timeline: 'Delivery Timeline',
      standardSpeed: 'Standard Delivery',
      expressSpeed: 'Express Priority (+25%)',
      relaxedSpeed: 'Relaxed Timeline (-10%)',
      estimatedCost: 'Estimated Investment',
      estimatedDays: 'Estimated Delivery',
      ctaButton: 'Request This Estimate',
      resetButton: 'Reset Calculator',
    },
    portfolio: {
      badge: 'Recent Work',
      title: 'Featured Projects',
      subtitle: 'Explore recent web applications, custom coded tools, WordPress portals, and modern client deployments.',
      filterAll: 'All Projects',
      filterDev: 'Development',
      filterDesign: 'Design & UI',
      filterWp: 'WordPress',
      viewLive: 'View Live Demo',
    },
    aiVideos: {
      badge: 'Viral Video Creation',
      title: 'AI Video Production & Commercials',
      subtitle: 'High-impact AI commercial ads, Instagram Reels, and CapCut video productions designed for viral engagement.',
      filterAll: 'All Videos',
      filterCommercials: 'Commercials & Ads',
      filterReels: 'Reels & Shorts',
      filterProducts: 'Product Showcases',
      playVideo: 'Play Video',
      closeModal: 'Close Video',
    },
    testimonials: {
      badge: 'Client Feedback',
      title: 'What Clients Say',
      subtitle: 'Trusted by business owners, founders, and marketing directors worldwide.',
      ratingText: '5.0 Star Rating on All Deliveries',
      verifiedBadge: 'Verified Client',
      items: [
        {
          id: 1,
          name: 'Sarah Jenkins',
          role: 'CEO, TechNova Solutions',
          content: 'Absolutely phenomenal work. The attention to detail, responsiveness, and sheer quality of the final product exceeded our expectations. Our new platform is incredibly fast and looks stunning.',
          avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=150&h=150',
        },
        {
          id: 2,
          name: 'David Chen',
          role: 'Founder, Elite Designs',
          content: 'Working closely on our redesign project was a breeze. They brought fresh ideas and executed modern UI/UX principles flawlessly. The entire process was professional and timely.',
          avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=150&h=150',
        },
        {
          id: 3,
          name: 'Emily Carter',
          role: 'Marketing Director, Vibe',
          content: 'The custom landing pages delivered have significantly increased our ROI. Truly engineered for high conversion rates. I am impressed by the seamless combination of design and speed optimization.',
          avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=150&h=150',
        },
        {
          id: 4,
          name: 'Michael Ross',
          role: 'Managing Partner, Ross & Co',
          content: 'Very professional. Transformed our outdated site into a modern, mobile-friendly experience. Highly recommend for any business looking to step up their digital presence.',
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=150&h=150',
        },
        {
          id: 5,
          name: 'Amina Al-Mansoor',
          role: 'Founder, Rohani Care Global',
          content: 'Exceeded all expectations in WordPress custom theme development and speed tuning. Our website speed score jumped to 98% on Google PageSpeed! Highly recommended developer.',
          avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=150&h=150',
        },
        {
          id: 6,
          name: 'Jason Vance',
          role: 'Head of Growth, Motral Media',
          content: 'The AI-generated video shorts and CapCut editing delivered millions of views on Instagram and YouTube Shorts. Creative cuts, auto captions, and super fast turnaround time.',
          avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=150&h=150',
        },
        {
          id: 7,
          name: 'Elena Rostova',
          role: 'Creative Lead, Studio X',
          content: 'Designed a pristine logo and vector brand identity that perfectly matched our company vision. The Canva graphics and Illustrator assets were delivered ready for print and digital use.',
          avatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&q=80&w=150&h=150',
        },
        {
          id: 8,
          name: 'Tariq Mahmood',
          role: 'Operations Lead, BizSkoop',
          content: 'Building our React and custom coded web platform with integrated AI capabilities was smooth and efficient. Exceptional communication and pristine code quality.',
          avatar: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&q=80&w=150&h=150',
        },
        {
          id: 9,
          name: 'Sophia Williams',
          role: 'E-commerce Owner, Bridal Luxe',
          content: 'Our WooCommerce store redesign was completed in record time. Conversion rates skyrocketed after the new sleek, mobile-optimized UI went live.',
          avatar: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&q=80&w=150&h=150',
        },
      ],
    },
    whyChooseMe: {
      badge: 'Key Advantages',
      title: 'Why Work With Me',
      subtitle: 'Dedicated to delivering exceptional quality, clear communication, and rapid turnaround.',
      description: 'I approach every project with meticulous technical precision, modern coding standards, and a deep commitment to high conversion and measurable business outcomes.',
      b1Title: 'Fast Delivery',
      b1Desc: 'Strict adherence to project timelines and milestones without sacrificing quality or performance.',
      b2Title: 'Clean & Modern Code',
      b2Desc: 'Pixel-perfect UI design with modern code standards, semantic HTML, and responsive mobile architecture.',
      b3Title: 'Mobile-First Design',
      b3Desc: 'Flawless presentation and touch interactions across smartphones, tablets, laptops, and ultra-wide screens.',
      b4Title: 'Client-Focused Partnership',
      b4Desc: 'Collaborative development approach focused on your strategic business goals and conversion results.',
    },
    contact: {
      badge: 'Get in Touch',
      title: "Let's Build Something Exceptional",
      subtitle: 'Have a project in mind or need a customized digital solution? Send a message and get a prompt response within 24 hours.',
      nameLabel: 'Your Full Name',
      namePlaceholder: 'e.g. John Doe',
      emailLabel: 'Email Address',
      emailPlaceholder: 'e.g. john@example.com',
      phoneLabel: 'Phone / WhatsApp (Optional)',
      phonePlaceholder: 'e.g. +1 555 123 4567',
      serviceLabel: 'Service of Interest',
      serviceSelectPlaceholder: 'Select a Service',
      messageLabel: 'Project Details & Requirements',
      messagePlaceholder: 'Tell me about your project, goals, timeline, and any specific requirements...',
      btnSend: 'Send Message',
      btnSending: 'Sending Message...',
      successTitle: 'Message Sent Successfully!',
      successMessage: 'Thank you for reaching out. I will review your message and reply via email/WhatsApp shortly.',
      errorMessage: 'Failed to send message. Please try again or reach out directly on WhatsApp.',
      directContact: 'Direct Inquiries',
      whatsappChat: 'Chat on WhatsApp',
    },
    footer: {
      description: 'Professional WordPress Web Developer, Custom Web Coder, and AI Video Editor dedicated to crafting high-performance digital experiences.',
      quickLinks: 'Quick Links',
      servicesTitle: 'Services',
      contactTitle: 'Contact Information',
      rightsReserved: 'All rights reserved.',
      developerCredit: 'Designed & Developed with Passion by Taibe Javed',
    },
    shortcuts: {
      title: 'Keyboard Shortcuts',
      subtitle: 'Fast navigation for power users & accessibility',
      pressToToggle: 'Press ? anywhere to toggle',
      escToClose: 'ESC to close',
      navigatingTo: 'Navigating to',
    },
  },
  ur: {
    nav: {
      home: 'ہوم',
      about: 'تعارف',
      skills: 'مہارتیں',
      services: 'خدمات',
      estimator: 'لاگت تخمینہ',
      work: 'پورٹ فولیو',
      portfolio: 'پورٹ فولیو',
      aiVideos: 'اے آئی ویڈیوز',
      testimonials: 'صارفین کی آراء',
      contact: 'رابطہ',
      letsTalk: 'بات چیت کریں',
      shortcuts: 'شارٹ کٹس',
    },
    hero: {
      badgeAvailable: 'نئے پروجیکٹس کے لیے دستیاب',
      badgeReadTime: '3 منٹ مطالعہ',
      name: 'تائب جاوید',
      titleLine: 'ورڈپریس ویب ڈویلپر | اے آئی اور کوڈڈ ویب سائٹس | اے آئی ویڈیو ایڈیٹر',
      fullParagraph: 'ہائی کنورٹنگ ورڈپریس ویب سائٹس، جدید اے آئی اور کسٹم کوڈڈ ویب سلوشنز (HTML/CSS/JS/React)، تخلیقی اے آئی ویڈیو ایڈیٹنگ (Reels/Shorts/CapCut) اور گرافک ڈیزائننگ میں مہارت۔',
      btnExplore: 'کام دیکھیں',
      btnContact: 'رابطہ کریں',
      portraitBadge: 'ورڈپریس • اے آئی • ویڈیو • ڈیزائن',
    },
    sectionDividers: {
      about: 'میرے بارے میں',
      skills: 'بنیادی مہارتیں',
      services: 'ہماری خدمات',
      estimator: 'پروجیکٹ لاگت تخمینہ',
      portfolio: 'نمایاں پروجیکٹس',
      aiVideos: 'اے آئی ویڈیو پروڈکشن',
      testimonials: 'صارفین کی آراء',
      whyChooseMe: 'مجھے کیوں منتخب کریں',
      contact: 'رابطہ کریں',
    },
    about: {
      badge: 'میرا سفر',
      title: 'جدید ڈیجیٹل حل',
      titleHighlight: 'اور اے آئی کی مہارت',
      subtitle: 'ایک پرجوش فل اسٹیک ورڈپریس ڈویلپر، کسٹم ویب کوڈر اور اے آئی ویڈیو اسپیشلسٹ جو بہترین ڈیجیٹل حل فراہم کرتا ہے۔',
      headingPrefix: 'ڈیجیٹل حل کی تخلیق',
      headingHighlight: 'بہترین معیار اور اے آئی کے ساتھ',
      bio: 'میں تائب جاوید ہوں، ایک مخلص ورڈپریس ڈویلپر، کسٹم ویب کریئٹر اور اے آئی ویڈیو پروڈیوسر جو رفتار، جدید ڈیزائن اور کاروباری نتائج پر توجہ مرکوز رکھتا ہے۔',
      tabDev: 'ویب ڈویلپمنٹ',
      tabVideo: 'اے آئی ویڈیو میڈیا',
      tabDesign: 'برانڈنگ اور UI',
      devTitle: 'ورڈپریس اور کسٹم ویب کوڈنگ',
      devDesc: 'تیز رفتار اور محفوظ ورڈپریس پورٹلز، ایلیمنٹر پرو اور ری ایکٹ کسٹم ویب ایپلیکیشنز کی تیاری۔',
      devTags: ['ورڈپریس', 'ایلیمنٹر پرو', 'وو کامرس', 'ری ایکٹ', 'HTML5/CSS3', 'SEO آپٹمائزیشن'],
      videoTitle: 'اے آئی ویڈیو پروڈکشن اور ریلز',
      videoDesc: 'کیپ کٹ اور جدید اے آئی ٹولز کے ساتھ انسٹاگرام ریلز، شارٹس اور کمرشل ویڈیو اشتہارات۔',
      videoTags: ['اے آئی ویڈیو', 'کیپ کٹ پرو', 'انسٹاگرام ریلز', 'آٹو کیپشنز', 'وائس اوور', 'وائرل فارمیٹس'],
      designTitle: 'لوگو اور گرافک ڈیزائن',
      designDesc: 'منفرد برانڈ شناخت، ویکٹر لوگوز، کینوا اور السٹریٹر/فوٹوشاپ میں اشتہاری گرافکس۔',
      designTags: ['لوگو ڈیزائن', 'برانڈ شناخت', 'کینوا پرو', 'السٹریٹر', 'فوٹوشاپ', 'سوشل میڈیا کٹ'],
      statYears: 'سال کا تجربہ',
      statDeployments: 'مکمل شدہ پروجیکٹس',
      btnStart: 'پروجیکٹ شروع کریں',
      cardName: 'تائب جاوید',
      cardRole: 'ویب ڈویلپر اور میڈیا کریئٹر',
      tabStory: 'میری کہانی',
      tabPhilosophy: 'طریقہ کار',
      tabTech: 'ٹیکنالوجی اسٹیک',
      storyP1: 'السلام علیکم! میں تائب جاوید ہوں، ایک مخلص ورڈپریس ڈویلپر، کسٹم ویب کریئٹر اور اے آئی ویڈیو پروڈیوسر۔',
      storyP2: 'مضبوط ورڈپریس پورٹلز، ریسپانسیو ویب ایپلی کیشنز اور وائرل اے آئی ویڈیو کمرشلز بنانے کے کئی سالہ تجربے کے ساتھ، میں آپ کے خیالات کو حقیقت میں بدلتا ہوں۔',
      storyP3: 'چاہے ای کامرس اسٹور بنانا ہو یا دلکش انسٹاگرام ریلز، میری ترجیح ہمیشہ تیز رفتار، بہترین ڈیزائن اور شاندار کارکردگی ہوتی ہے۔',
      statExp: '4+',
      statExpLabel: 'سال کا تجربہ',
      statProjects: '50+',
      statProjectsLabel: 'کامیاب پروجیکٹس',
      statSatisfaction: '100%',
      statSatisfactionLabel: 'گاہکوں کا اطمینان',
      quote: '"بہترین ڈیجیٹل حل تخلیقی ڈیزائن اور معیاری کوڈنگ کا حسین امتزاج ہوتے ہیں۔"',
      quoteAuthor: 'تائب جاوید',
    },
    skills: {
      badge: 'تکنیکی صلاحیتیں',
      title: 'بنیادی تکنیکی مہارتیں',
      subtitle: 'جدید ویب ٹیکنالوجیز، اے آئی ٹولز اور تخلیقی ڈیزائننگ میں سالہا سال کی مہارت۔',
      headingPrefix: 'میری بنیادی',
      headingHighlight: 'تکنیکی مہارتیں',
      subheading: 'جدید ویب ٹیکنالوجیز، کسٹم کوڈنگ، اے آئی ویڈیو پروڈکشن اور گرافک ڈیزائننگ میں جامع مہارت۔',
      proficiency: 'مہارت',
    },
    services: {
      badge: 'ہماری خدمات',
      title: 'کاروباری ترقی کے لیے خدمات',
      subtitle: 'دنیا بھر کے کاروباروں کے لیے جامع ڈیجیٹل ڈویلپمنٹ، کسٹم کوڈنگ اور اے آئی ویڈیو پروڈکشن۔',
      headingPrefix: 'ہماری خصوصی',
      headingHighlight: 'ڈیجیٹل خدمات',
      subheading: 'آپ کے کاروبار کو تیز رفتار ترقی اور آن لائن کامیابی دلانے کے لیے معیاری ڈیجیٹل سلوشنز۔',
      btnOrder: 'سروس بُک کریں',
      inquireNow: 'معلومات حاصل کریں',
      featuresIncluded: 'شامل نمایاں خصوصیات',
      items: [
        {
          title: 'ورڈپریس ویب ڈویلپمنٹ',
          description: 'کسٹم ورڈپریس سائٹس، ایلیمنٹر پرو لے آؤٹس، وو کامرس اسٹورز اور تیز رفتار و محفوظ سیٹ اپ۔',
          iconName: 'Globe',
          tags: ['ورڈپریس', 'ایلیمنٹر', 'وو کامرس'],
        },
        {
          title: 'اے آئی اور کسٹم کوڈڈ ویب سائٹس',
          description: 'جدید اے آئی جنریشن اور کسٹم HTML، CSS، JavaScript، اور React کے ساتھ تیز رفتار ویب سائٹس۔',
          iconName: 'Code2',
          tags: ['اے آئی بلڈر', 'کسٹم کوڈ', 'ری ایکٹ'],
        },
        {
          title: 'اے آئی ویڈیو ایڈیٹنگ اور شارٹس',
          description: 'تخلیقی اے آئی ویڈیو کمرشلز، انسٹاگرام ریلز، یوٹیوب شارٹس اور کیپ کٹ آڈیو ڈیزائن۔',
          iconName: 'Zap',
          tags: ['اے آئی ویڈیو', 'کیپ کٹ', 'ریلز اور شارٹس'],
        },
        {
          title: 'لوگو اور گرافک ڈیزائن',
          description: 'منفرد برانڈ شناخت، ویکٹر لوگوز، کینوا ٹیمپلیٹس اور السٹریٹر/فوٹوشاپ گرافکس۔',
          iconName: 'PenTool',
          tags: ['برانڈنگ', 'کینوا', 'السٹریٹر'],
        },
        {
          title: 'ویب سائٹ ری ڈیزائننگ',
          description: 'پرانی یا سست رفتار ویب سائٹس کو جدید، موبائل فرینڈلی اور خوبصورت شکل میں تبدیل کرنا۔',
          iconName: 'Layers',
          tags: ['ری ڈیزائن', 'جدید UI', 'موبائل فرسٹ'],
        },
        {
          title: 'اسپیڈ اور SEO اصلاحات',
          description: 'ویب سائٹ لوڈ ٹائم کو 2 سیکنڈ سے کم کرنا اور گوگل پر رینکنگ کے لیے مکمل SEO سٹرکچر۔',
          iconName: 'Zap',
          tags: ['لائٹ ہاؤس', 'اسپیڈ بوسٹ', 'SEO'],
        },
      ],
    },
    calculator: {
      badge: 'شفاف قیمتیں',
      title: 'پروجیکٹ لاگت تخمینہ',
      subtitle: 'اپنے پروجیکٹ کی تفصیلات منتخب کریں اور فوری طور پر لاگت اور وقت کا تخمینہ حاصل کریں۔',
      step1Title: '1. بنیادی سروس منتخب کریں',
      step2Title: '2. اضافی فیچرز شامل کریں',
      step3Title: '3. ٹائم لائن اور حتمی لاگت',
      timeline: 'ڈیلیوری ٹائم لائن',
      standardSpeed: 'معیاری ڈیلیوری',
      expressSpeed: 'فاسٹ ڈیلیوری (+25%)',
      relaxedSpeed: 'آرام دہ ٹائم لائن (-10%)',
      estimatedCost: 'تخمینی لاگت',
      estimatedDays: 'تخمینی دن',
      ctaButton: 'یہ تخمینہ آگے بڑھائیں',
      resetButton: 'دوبارہ شروع کریں',
    },
    portfolio: {
      badge: 'ہمارا کام',
      title: 'نمایاں پروجیکٹس',
      subtitle: 'حال ہی میں مکمل کیے گئے ویب ایپلیکیشنز، کسٹم ٹولز اور ورڈپریس پورٹلز دیکھیں۔',
      filterAll: 'تمام پروجیکٹس',
      filterDev: 'ڈویلپمنٹ',
      filterDesign: 'ڈیزائن اور UI',
      filterWp: 'ورڈپریس',
      viewLive: 'لائیو پروجیکٹ دیکھیں',
    },
    aiVideos: {
      badge: 'ویڈیو پروڈکشن',
      title: 'اے آئی ویڈیو کمرشلز اور ریلز',
      subtitle: 'وائرل سوشل میڈیا مواد، اے آئی کمرشل اشتہارات اور جدید ویڈیو ایڈیٹنگ۔',
      filterAll: 'تمام ویڈیوز',
      filterCommercials: 'کمرشلز اور اشتہارات',
      filterReels: 'ریلز اور شارٹس',
      filterProducts: 'پروڈکٹ نمائش',
      playVideo: 'ویڈیو چلائیں',
      closeModal: 'ویڈیو بند کریں',
    },
    testimonials: {
      badge: 'کلائنٹ کی رائے',
      title: 'ہمارے صارفین کیا کہتے ہیں',
      subtitle: 'دنیا بھر کے مختلف کاروباری مالکان اور اداروں کا ہم پر بھروسہ۔',
      ratingText: 'تمام پروجیکٹس پر 5.0 ریٹنگ',
      verifiedBadge: 'تصدیق شدہ کلائنٹ',
      items: [
        {
          id: 1,
          name: 'سارہ جینکنز',
          role: 'سی ای او، ٹیک نووا سلوشنز',
          content: 'واقعی شاندار اور بے مثال کام! باریک بینی، فوری رسپانس اور حتمی کام کا معیار ہماری توقعات سے کہیں بڑھ کر تھا۔ ہمارا نیا پلیٹ فارم انتہائی تیز رفتار اور دلکش ہے۔',
          avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=150&h=150',
        },
        {
          id: 2,
          name: 'ڈیوڈ چن',
          role: 'بانی، ایلیٹ ڈیزائنز',
          content: 'ہماری ویب سائٹ کی ری ڈیزائننگ پر تائب کے ساتھ کام کرنے کا تجربہ بہت لاجواب رہا۔ انہوں نے جدید ترین UI/UX اور عمدہ آئیڈیاز کو نہایت مہارت سے نافذ کیا۔ پورا کام پروفیشنل اور بروقت تھا۔',
          avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=150&h=150',
        },
        {
          id: 3,
          name: 'ایملی کارٹر',
          role: 'مارکیٹنگ ڈائریکٹر، وائب',
          content: 'تیار کردہ کسٹم لینڈنگ پیجز نے ہمارے کاروباری نتائج اور سیلز میں زبردست اضافہ کیا۔ جدید ڈیزائن اور اسپیڈ آپٹمائزیشن کا ایسا امتزاج واقعی قابلِ تحسین ہے۔',
          avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=150&h=150',
        },
        {
          id: 4,
          name: 'مائیکل راس',
          role: 'مینیجنگ پارٹنر، راس اینڈ کمپنی',
          content: 'انتہائی پیشہ ورانہ انداز! ہماری پرانی سائٹ کو ایک جدید اور مکمل موبائل فرینڈلی پلیٹ فارم میں بدل دیا۔ ہر اس کاروبار کے لیے پرزور سفارش ہے جو اپنی آن لائن شناخت بہتر بنانا چاہتا ہے۔',
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=150&h=150',
        },
        {
          id: 5,
          name: 'آمنہ المنصور',
          role: 'بانی، روحانی کیئر گلوبل',
          content: 'ورڈپریس کسٹم تھیم ڈویلپمنٹ اور اسپیڈ ٹیوننگ میں توقعات سے بڑھ کر نتائج دیے۔ گوگل پیج اسپیڈ پر ہماری ویب سائٹ کا اسکور 98 فیصد تک پہنچ گیا! شاندار ڈویلپر ہیں۔',
          avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=150&h=150',
        },
        {
          id: 6,
          name: 'جیسن وینس',
          role: 'ہیڈ آف گروتھ، موٹرل میڈیا',
          content: 'اے آئی ویڈیو شارٹس اور کیپ کٹ ایڈیٹنگ کی بدولت ہمارے انسٹاگرام اور یوٹیوب پر لاکھوں ویوز آئے۔ شاندار تخلیقی کٹس، بہترین آٹو کیپشنز اور تیز ترین ڈیلیوری۔',
          avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=150&h=150',
        },
        {
          id: 7,
          name: 'ایلینا روستووا',
          role: 'کریئیٹو لیڈ، اسٹوڈیو ایکس',
          content: 'ہماری کمپنی کے وژن کے عین مطابق ایک شاندار لوگو اور ویکٹر برانڈ شناخت ڈیزائن کی۔ کینوا اور السٹریٹر کے تمام گرافکس پرنٹ اور ڈیجیٹل استعمال کے لیے مکمل تیار ملے۔',
          avatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&q=80&w=150&h=150',
        },
        {
          id: 8,
          name: 'طارق محمود',
          role: 'آپریشنز لیڈ، بز اسکوپ',
          content: 'ہمارا ری ایکٹ اور کسٹم کوڈڈ ویب پلیٹ فارم جدید اے آئی انٹیگریشن کے ساتھ نہایت کامیابی سے تیار کیا گیا۔ شاندار رابطہ کاری اور بہترین کوڈ کوالٹی۔',
          avatar: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&q=80&w=150&h=150',
        },
        {
          id: 9,
          name: 'صوفیہ ولیمز',
          role: 'ای کامرس اونر، برائیڈل لکس',
          content: 'ہمارے وو کامرس اسٹور کی ری ڈیزائننگ ریکارڈ وقت میں مکمل ہوئی۔ نئے پرکشش اور موبائل آپٹیمائزڈ ڈیزائن کے بعد کسٹمرز کے آرڈرز اور سیلز میں زبردست اضافہ ہوا۔',
          avatar: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&q=80&w=150&h=150',
        },
      ],
    },
    whyChooseMe: {
      badge: 'ہماری خصوصیات',
      title: 'مجھے کیوں منتخب کریں؟',
      subtitle: 'اعلیٰ معیار، واضح رابطہ اور بروقت ڈیلیوری کا پختہ عزم۔',
      description: 'میں ہر پروجیکٹ کو انتہائی لگن، جدید کوڈنگ معیارات اور کاروباری نتائج کو سامنے رکھ کر مکمل کرتا ہوں۔',
      b1Title: 'بروقت ڈیلیوری',
      b1Desc: 'پروجیکٹ کی مقررہ ڈیڈ لائن کا احترام اور معیار پر کوئی سمجھوتہ نہیں۔',
      b2Title: 'صاف ستھرا جدید کوڈ',
      b2Desc: 'جدید کوڈنگ معیار، بہترین ڈیزائن اور مکمل رسپانسیو اسٹرکچر۔',
      b3Title: 'موبائل فرینڈلی ڈیزائن',
      b3Desc: 'موبائل، ٹیبلٹ اور ڈیسک ٹاپ تمام اسکرینز پر شاندار اور ہموار کارکردگی۔',
      b4Title: 'کلائنٹ پر مرکوز حکمت عملی',
      b4Desc: 'آپ کے کاروباری اہداف اور کامیابی کو سامنے رکھ کر کام کرنا۔',
    },
    contact: {
      badge: 'رابطہ کریں',
      title: 'آئیے مل کر کچھ شاندار بنائیں',
      subtitle: 'کوئی نیا پروجیکٹ شروع کرنا چاہتے ہیں؟ ہمیں میسج بھیجیں، 24 گھنٹے میں جواب دیا جائے گا۔',
      nameLabel: 'آپ کا پورا نام',
      namePlaceholder: 'مثال: محمد احمد',
      emailLabel: 'ای میل ایڈریس',
      emailPlaceholder: 'مثال: ahmed@example.com',
      phoneLabel: 'فون / واٹس ایپ (اختیاری)',
      phonePlaceholder: 'مثال: 923001234567+',
      serviceLabel: 'مطلوبہ سروس',
      serviceSelectPlaceholder: 'سروس منتخب کریں',
      messageLabel: 'پروجیکٹ کی تفصیلات',
      messagePlaceholder: 'اپنے پروجیکٹ، مقاصد، ٹائم لائن اور ضروریات کے بارے میں بتائیں...',
      btnSend: 'پیغام بھیجیں',
      btnSending: 'پیغام بھیجا جا رہا ہے...',
      successTitle: 'پیغام کامیابی سے بھیج دیا گیا!',
      successMessage: 'رابطہ کرنے کا شکریہ۔ میں جلد آپ سے ای میل یا واٹس ایپ کے ذریعے رابطہ کروں گا۔',
      errorMessage: 'پیغام بھیجنے میں مسئلہ پیش آیا۔ براہ کرم دوبارہ کوشش کریں یا واٹس ایپ پر رابطہ کریں۔',
      directContact: 'براہِ راست رابطہ',
      whatsappChat: 'واٹس ایپ پر چیٹ کریں',
    },
    footer: {
      description: 'پیشہ ورانہ ورڈپریس ویب ڈویلپر، کسٹم ویب کوڈر اور اے آئی ویڈیو ایڈیٹر جو شاندار ڈیجیٹل تجربات تخلیق کرتا ہے۔',
      quickLinks: 'فوری لنکس',
      servicesTitle: 'خدمات',
      contactTitle: 'رابطے کی معلومات',
      rightsReserved: 'جملہ حقوق محفوظ ہیں۔',
      developerCredit: 'تائب جاوید کی طرف سے محبت اور خلوص سے تیار کردہ',
    },
    shortcuts: {
      title: 'کی بورڈ شارٹ کٹس',
      subtitle: 'تیز تر نیویگیشن اور آسانی کے لیے',
      pressToToggle: 'شارٹ کٹس دیکھنے کے لیے ? دبائیں',
      escToClose: 'بند کرنے کے لیے ESC دبائیں',
      navigatingTo: 'روانگی برائے',
    },
  },
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
  t: Translations;
  isUrdu: boolean;
  dir: 'ltr' | 'rtl';
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    try {
      const saved = localStorage.getItem('portfolio_preferred_language') as Language;
      return saved === 'ur' || saved === 'en' ? saved : 'en';
    } catch {
      return 'en';
    }
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem('portfolio_preferred_language', lang);
    } catch {
      // ignore
    }
  };

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'ur' : 'en');
  };

  const isUrdu = language === 'ur';
  const dir = isUrdu ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = dir;
    if (isUrdu) {
      document.body.classList.add('lang-urdu');
    } else {
      document.body.classList.remove('lang-urdu');
    }
  }, [language, isUrdu, dir]);

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        toggleLanguage,
        t: TRANSLATIONS[language],
        isUrdu,
        dir,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
